<script lang="ts">
  import { tick } from "svelte";
  import { fade } from "svelte/transition";
  import { orgUndo } from "@nrk/origo";

  import { orgClose } from "@nrk/origo";

  export let src: string;
  export let srcHighRes: string | undefined;
  export let setIsFullscreen: (value: boolean) => void;
  export let imageType: string | undefined;
  export let isFullscreen: boolean = false;

  const MINIMAP_WIDTH = 180;
  const MIN_ZOOM = 1;
  const MAX_ZOOM = 10;

  let viewportEl: HTMLElement;
  let proxyImgEl: HTMLImageElement;

  let zoom = 1;
  let panX = 0;
  let panY = 0;
  let coverW = 0;
  let coverH = 0;
  let minimapHeight = 135;
  let highResLoaded = false;
  let highResImgEl: HTMLImageElement;
  let minimapContainerEl: HTMLElement;
  let minimapDragging = false;
  let lastTouchX = 0;
  let lastTouchY = 0;

  let boxLeft = 0;
  let boxTop = 0;
  let boxWidth = MINIMAP_WIDTH;
  let boxHeight = minimapHeight;

  // Reset when the image changes
  $: srcHighRes, (highResLoaded = false);

  // Layer high-res background image on top of proxy — proxy stays visible until high-res is ready
  $: bgImages = highResLoaded && srcHighRes ? `url(${srcHighRes}), url(${src})` : `url(${src})`;
  $: bgLayerSize = (s: string) => (highResLoaded && srcHighRes ? `${s}, ${s}` : s);

  /*    Natural coordinates: Uses image's natural pixel dimensions as reference
      Display coordinates: Calculates how image appears in viewport
      Pan coordinates: Tracks offset from top-left corner
      Minimap coordinates: Scales everything down to minimap dimensions */

  $: bgStyle = coverW
    ? `background-image: ${bgImages}; background-size: ${bgLayerSize(
        `${coverW * zoom}px ${coverH * zoom}px`,
      )}; background-position: ${bgLayerSize(`${panX}px ${panY}px`)}; background-repeat: no-repeat`
    : `background-image: ${bgImages}; background-size: ${bgLayerSize(
        "contain",
      )}; background-position: ${bgLayerSize("center")}; background-repeat: no-repeat`;

  $: onIsFullscreenChange(isFullscreen);

  async function onIsFullscreenChange(value: boolean) {
    if (value) {
      await tick(); // wait for dialog to become visible so offsetWidth is non-zero
      zoom = 1;
      computeCoverSize();
      initPan();
      updateMinimap();
    }
  }

  function handleDialogEvent(this: HTMLElement, event: Event) {
    const target = event.target as HTMLElement;
    if (this === target) {
      setIsFullscreen(!target.hidden);
    }
  }

  function computeCoverSize() {
    /*  
    This function calculates the display dimensions of an image when it's scaled to fit entirely 
    within a container while maintaining its aspect ratio (similar to CSS object-fit: contain).
    */
    if (!proxyImgEl?.naturalWidth || !viewportEl) return;
    const imgW = proxyImgEl.naturalWidth;
    const imgH = proxyImgEl.naturalHeight;
    const contW = viewportEl.offsetWidth;
    const contH = viewportEl.offsetHeight;
    if (!contW || !contH) return;
    const containScale = Math.min(contW / imgW, contH / imgH);
    coverW = imgW * containScale;
    coverH = imgH * containScale;
  }

  function initPan() {
    if (!viewportEl) return;
    panX = (viewportEl.offsetWidth - coverW) / 2;
    panY = (viewportEl.offsetHeight - coverH) / 2;
  }

  function clampPan() {
    /* This function constrains the pan position to prevent users from panning 
    beyond the image boundaries, ensuring the image always stays within reasonable 
    viewing bounds.*/
    if (!viewportEl) return;
    const contW = viewportEl.offsetWidth;
    const contH = viewportEl.offsetHeight;
    const dispW = coverW * zoom;
    const dispH = coverH * zoom;
    panX = dispW <= contW ? (contW - dispW) / 2 : Math.min(0, Math.max(panX, contW - dispW));
    panY = dispH <= contH ? (contH - dispH) / 2 : Math.min(0, Math.max(panY, contH - dispH));
  }

  function onProxyLoad() {
    if (proxyImgEl?.naturalWidth) {
      minimapHeight = (MINIMAP_WIDTH / proxyImgEl.naturalWidth) * proxyImgEl.naturalHeight;
    }
    computeCoverSize();
    initPan();
    updateMinimap();
  }

  function handleViewportClick(event: MouseEvent) {
    if (minimapContainerEl?.contains(event.target as Node)) return;
    if (zoom > MIN_ZOOM) {
      zoom = 1;
      initPan();
      updateMinimap();
    } else {
      const newZoom = 2;
      const scale = newZoom / zoom;
      panX = event.offsetX - (event.offsetX - panX) * scale;
      panY = event.offsetY - (event.offsetY - panY) * scale;
      zoom = newZoom;
      clampPan();
      updateMinimap();
    }
  }

  // Focus-based panning: only active when zoomed in
  function pan(event: MouseEvent) {
    if (!viewportEl || !coverW || minimapDragging || zoom <= MIN_ZOOM) return;
    if (minimapContainerEl?.contains(event.target as Node)) return;
    const contW = viewportEl.offsetWidth;
    const contH = viewportEl.offsetHeight;
    const dispW = coverW * zoom;
    const dispH = coverH * zoom;

    // Use coordinates relative to viewport instead of event target
    const rect = viewportEl.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;

    panX = dispW > contW ? -(offsetX / contW) * (dispW - contW) : (contW - dispW) / 2;
    panY = dispH > contH ? -(offsetY / contH) * (dispH - contH) : (contH - dispH) / 2;
    updateMinimap();
  }

  function handleWheel(event: WheelEvent) {
    //if (!event.metaKey) return; // let browser handle non-cmd scroll normally
    if (!coverW) return;
    event.preventDefault();

    const factor = event.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom * factor));
    if (newZoom === zoom) return;

    // Keep the image point under the mouse cursor fixed
    const scale = newZoom / zoom;
    panX = event.offsetX - (event.offsetX - panX) * scale;
    panY = event.offsetY - (event.offsetY - panY) * scale;
    zoom = newZoom;

    clampPan();
    updateMinimap();
  }

  function updateMinimap() {
    if (!proxyImgEl?.naturalWidth || !viewportEl || !coverW) return;

    const imgW = proxyImgEl.naturalWidth;
    const imgH = proxyImgEl.naturalHeight;
    const contW = viewportEl.offsetWidth;
    const contH = viewportEl.offsetHeight;
    const dispW = coverW * zoom;
    const dispH = coverH * zoom;

    // Visible region in natural image coordinates
    const visX = (-panX / dispW) * imgW;
    const visY = (-panY / dispH) * imgH;
    const visW = (contW / dispW) * imgW;
    const visH = (contH / dispH) * imgH;

    const ms = MINIMAP_WIDTH / imgW;
    boxLeft = Math.max(0, visX * ms);
    boxTop = Math.max(0, visY * ms);
    boxWidth = Math.min(visW * ms, MINIMAP_WIDTH - boxLeft);
    boxHeight = Math.min(visH * ms, minimapHeight - boxTop);
  }

  function applyMinimapPan(clientX: number, clientY: number) {
    if (!minimapContainerEl || !viewportEl || !coverW) return;
    const rect = minimapContainerEl.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, MINIMAP_WIDTH));
    const y = Math.max(0, Math.min(clientY - rect.top, minimapHeight));
    const contW = viewportEl.offsetWidth;
    const contH = viewportEl.offsetHeight;
    panX = contW / 2 - (x / MINIMAP_WIDTH) * (coverW * zoom);
    panY = contH / 2 - (y / minimapHeight) * (coverH * zoom);
    clampPan();
    updateMinimap();
  }

  function handleMinimapMousedown(event: MouseEvent) {
    minimapDragging = true;
    applyMinimapPan(event.clientX, event.clientY);
  }

  function handleWindowMousemove(event: MouseEvent) {
    if (minimapDragging) applyMinimapPan(event.clientX, event.clientY);
  }

  function handleWindowMouseup() {
    minimapDragging = false;
  }

  function handleTouchStart(event: TouchEvent) {
    if (event.touches.length !== 1) return;
    lastTouchX = event.touches[0].clientX;
    lastTouchY = event.touches[0].clientY;
  }

  function handleTouchMove(event: TouchEvent) {
    if (!viewportEl || !coverW || event.touches.length !== 1) return;
    if (zoom <= MIN_ZOOM) return;
    event.preventDefault();
    const dx = event.touches[0].clientX - lastTouchX;
    const dy = event.touches[0].clientY - lastTouchY;
    lastTouchX = event.touches[0].clientX;
    lastTouchY = event.touches[0].clientY;
    panX += dx;
    panY += dy;
    clampPan();
    updateMinimap();
  }

  let orientationChanging = false;

  function reflow() {
    computeCoverSize();
    if (zoom <= MIN_ZOOM) {
      initPan();
    } else {
      clampPan();
    }
    updateMinimap();
  }

  function handleResize() {
    if (orientationChanging) return;
    reflow();
  }

  function handleOrientationChange() {
    orientationChanging = true;
    // debounce to avoid excessive recalculations
    setTimeout(() => {
      orientationChanging = false;
      reflow();
    }, 200);
  }
</script>

<svelte:window
  on:resize={handleResize}
  on:orientationchange={handleOrientationChange}
  on:mousemove={handleWindowMousemove}
  on:mouseup={handleWindowMouseup}
/>

<bb-dialog class="fullscreen-dialog" hidden={!isFullscreen} on:dialog-toggle={handleDialogEvent}>
  <div
    class="viewport"
    class:pannable={imageType !== "HEIC" && zoom <= MIN_ZOOM}
    class:zoomout={imageType !== "HEIC" && zoom > MIN_ZOOM}
    bind:this={viewportEl}
    role="button"
    tabindex="0"
    on:click={imageType !== "HEIC" ? handleViewportClick : undefined}
    on:keydown={(e) => {
      if ((e.key === "Enter" || e.key === " ") && imageType !== "HEIC") {
        handleViewportClick(new MouseEvent("click", { bubbles: true }));
      }
    }}
    on:mousemove={imageType !== "HEIC" ? pan : undefined}
    on:wheel|nonpassive={handleWheel}
    on:touchstart={handleTouchStart}
    on:touchmove|nonpassive={handleTouchMove}
    style={bgStyle}
  >
    <button
      class="close-button"
      aria-label="lukk fullskjerm"
      on:click|stopPropagation={() => setIsFullscreen(false)}>{@html orgClose}</button
    >
    <img bind:this={proxyImgEl} {src} alt="" class="proxy-hidden" on:load={onProxyLoad} />
    {#if isFullscreen && srcHighRes}
      <img
        bind:this={highResImgEl}
        src={srcHighRes}
        alt=""
        class="proxy-hidden"
        on:load={() => (highResLoaded = true)}
      />
    {/if}

    {#if imageType !== "HEIC" && zoom > MIN_ZOOM}
      <div class="info-container" transition:fade={{ duration: 200 }}>
        <div
          class="minimap-container"
          class:minimap-dragging={minimapDragging}
          style={`height: ${minimapHeight}px`}
          bind:this={minimapContainerEl}
          role="button"
          tabindex="0"
          aria-label="Kart over bildet - trykk for å navigere til sentrum"
          on:mousedown={handleMinimapMousedown}
          on:keydown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              // Simulate click at center of minimap
              const rect = minimapContainerEl.getBoundingClientRect();
              const centerX = rect.left + rect.width / 2;
              const centerY = rect.top + rect.height / 2;
              applyMinimapPan(centerX, centerY);
            }
          }}
        >
          <img draggable="false" {src} alt="Kart over hele bildet" class="minimap-img" />

          <div
            class="minimap-current-view"
            style={`left: ${boxLeft}px; top: ${boxTop}px; width: ${boxWidth}px; height: ${boxHeight}px`}
          ></div>
        </div>
        <div class="info-text">
          {Math.round(zoom * 100)}%
          <button
            class="org-button"
            on:click|stopPropagation={() => {
              zoom = 1;
              initPan();
              updateMinimap();
            }}>{@html orgUndo}</button
          >
          <h5>{highResLoaded ? "Full" : "Lav"}</h5>
        </div>
      </div>
    {/if}
  </div>
</bb-dialog>

<style>
  .fullscreen-dialog {
    max-width: none;
    max-height: none;
    position: absolute;
    inset: 2.5%;
    width: auto;
    height: 95dvh;
  }

  .close-button {
    position: absolute;
    top: var(--org-medium);
    right: var(--org-medium);
    background: none;
    border: none;
    cursor: pointer;
    color: var(--org-color-white);
    padding: var(--org-xsmall);
    z-index: 1;
  }

  .close-button:hover {
    background-color: var(--org-color-shade-1);
  }

  .viewport {
    width: 100%;
    height: 100%;
    position: relative;
    overflow: hidden;
  }

  .pannable {
    cursor: zoom-in;
  }

  .zoomout {
    cursor: zoom-out;
  }

  .proxy-hidden {
    display: none;
  }

  .info-container {
    position: absolute;
    bottom: max(2rem, env(safe-area-inset-bottom));
    right: max(2rem, env(safe-area-inset-right));
    width: 180px;
  }
  .info-text {
    background: var(--color-surface-secondary);
    color: var(--color-text-primary);
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    height: var(--org-large);
  }
  .minimap-container {
    border: 2px solid var(--color-surface-secondary);
    box-shadow: 0 2px 8px rgba(var(--org-color-black), 0.6);
    overflow: hidden;
    cursor: grab;
  }

  .minimap-dragging {
    cursor: grabbing;
  }
  .minimap-img {
    display: block;
    width: 100%;
    height: 100%;
  }
  .minimap-current-view {
    position: absolute;
    border: 2px solid var(--org-color-white);
  }
</style>
